import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UnitRow from "./UnitRow";
import BulkApplyRow from "./BulkApplyRow";

// "Scannable Units" mode: every unit across every item in one table (mockup
// renderFlatUnitsView), with its own Booked/Re-Weighed toggle and its own
// selection — independent from any per-item card's selection, per the plan.
// Rows reuse UnitRow (with showItemColumn/itemName) rather than duplicating
// the row markup, so booked/re-weigh editing, the kebab menu, history, and
// swap all behave identically here and inside a per-item UnitsTable.
// Edits here are DEFERRED into the same container-owned pendingUnitEdits the
// Items view uses (via onUnitPendingEdit) and flushed on the box's Save — so
// nothing auto-saves while typing, and the two views are just two windows onto
// the same pending edits (the old cross-view lock is no longer needed).
const EMPTY_FIELD_EDITS = {};

const FlatUnitsView = ({ items = [], readOnly = false, pendingUnitEdits = {}, onUnitPendingEdit }) => {
  const [mode, setMode] = useState("booked");
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const headerCheckboxRef = useRef(null);

  const rows = useMemo(() => {
    const flattened = [];
    (items || []).forEach((item) => {
      (item.units || []).forEach((unit) => {
        flattened.push({ item, unit });
      });
    });
    return flattened;
  }, [items]);

  const selectableRows = rows;

  // Selection is intentionally NOT cleared when the view locks — read-only
  // view supports selecting units for the Download-labels bulk action (user
  // request). BulkApplyRow renders without edit fields/Apply in read-only, so
  // no stale write path survives.

  // Same "drop stale selected ids" guard ItemCard applies for its own
  // per-item selection, just scoped to the full cross-item unit set.
  useEffect(() => {
    setCheckedIds((prev) => {
      const validIds = new Set(rows.map((r) => r.unit.id));
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [rows]);

  // IM11: UnitRow is React.memo'd, but FlatUnitsView re-renders on every
  // keystroke in the tree (grid state lives in the parent). Passing fresh inline
  // callbacks per row would defeat that memo and re-render EVERY row of the
  // cross-item scannable-units table on each keystroke. Stabilize the callbacks
  // (the context value is already keystroke-stable) so memo can skip untouched
  // rows.
  const handleToggleCheck = useCallback((unitId) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(
    (nextChecked) => {
      setCheckedIds(nextChecked ? new Set(rows.map((r) => r.unit.id)) : new Set());
    },
    [rows]
  );

  // Stable per-unit onPendingEdit: a latest-ref keeps the cached callbacks from
  // going stale, and identity is fixed per unit id so React.memo(UnitRow) sees
  // the same reference across renders.
  const onUnitPendingEditRef = useRef(onUnitPendingEdit);
  onUnitPendingEditRef.current = onUnitPendingEdit;
  const rowPendingEditRef = useRef(new Map());
  const getRowPendingEdit = (itemId, unitId) => {
    let cb = rowPendingEditRef.current.get(unitId);
    if (!cb) {
      cb = (fields) =>
        onUnitPendingEditRef.current && onUnitPendingEditRef.current(itemId, "existing", unitId, fields);
      rowPendingEditRef.current.set(unitId, cb);
    }
    return cb;
  };
  // unitId -> itemId map so the bulk-apply callback resolves the item in O(1)
  // instead of rows.find() per selected unit (O(selected x rows)).
  const unitItemMap = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.unit.id, r.item.id));
    return m;
  }, [rows]);
  const handleBulkUnitPendingEdit = useCallback(
    (kind, unitId, fields) => {
      const itemId = unitItemMap.get(unitId);
      if (itemId && onUnitPendingEditRef.current) onUnitPendingEditRef.current(itemId, kind, unitId, fields);
    },
    [unitItemMap]
  );
  const handleClear = useCallback(() => handleToggleAll(false), [handleToggleAll]);

  const checkedCount = selectableRows.filter((r) => checkedIds.has(r.unit.id)).length;
  const allChecked = selectableRows.length > 0 && checkedCount === selectableRows.length;
  const indeterminate = checkedCount > 0 && !allChecked;

  const effectiveCheckedIds = checkedIds;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate, selectableRows.length]);

  return (
    <div className="flat-view">
      <div className="flat-view-toolbar">
        <div className="scan-title">Scannable Units ({rows.length})</div>
        <div className="mode-toggle">
          <button
            type="button"
            className={`mode-btn ${mode !== "reweigh" ? "active" : ""}`}
            onClick={() => setMode("booked")}
          >
            Booked
          </button>
          <button
            type="button"
            className={`mode-btn ${mode === "reweigh" ? "active" : ""}`}
            onClick={() => setMode("reweigh")}
          >
            Re-Weigh
          </button>
        </div>
      </div>
      <div className="table-scroll">
        <table className="scan-table flat-table">
          <thead>
            <tr>
              <th>
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  className="rowcheck"
                  checked={allChecked}
                  disabled={rows.length === 0}
                  onChange={() => handleToggleAll(!allChecked)}
                />
              </th>
              <th>Unit Label</th>
              <th>Piece Split</th>
              <th>Weight</th>
              <th>Dimensions (L&times;W&times;H)</th>
              <th>Storage Location</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {/* Mounts whenever something is selected, read-only included —
                BulkApplyRow drops its edit fields/Apply in read-only and shows
                only the count/Clear/Download-labels area, so no write path
                survives a locked box. */}
            {effectiveCheckedIds.size > 0 && (
              <BulkApplyRow
                checkedIds={effectiveCheckedIds}
                mode={mode}
                readOnly={readOnly}
                onClear={handleClear}
                deferSaves={!readOnly}
                onUnitPendingEdit={handleBulkUnitPendingEdit}
              />
            )}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="flat-empty">
                  No scannable units yet &mdash; add items and handling units to see them here.
                </td>
              </tr>
            ) : (
              rows.map(({ item, unit }) => (
                <UnitRow
                  key={unit.id}
                  item={item}
                  unit={unit}
                  mode={mode}
                  checked={effectiveCheckedIds.has(unit.id)}
                  onCheck={handleToggleCheck}
                  readOnly={readOnly}
                  deferSaves={!readOnly}
                  pendingEdits={((pendingUnitEdits[item.id] || {}).existing || {})[unit.id] || EMPTY_FIELD_EDITS}
                  onPendingEdit={getRowPendingEdit(item.id, unit.id)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FlatUnitsView;
